import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
  CollectionCollage,
} from "@/components/store";
import { useCart } from "../../context/CartContext";
import { useStore } from "../../hooks/useStore";
import { getSubdomain } from "../../utils/storeUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreContactUs() {
  const { subdomain: paramSubdomain } = useParams();
  const subdomain = paramSubdomain || getSubdomain();
  const navigate = useNavigate();

  // Use custom hook for all store data
  const { 
    store, 
    theme, 
    storeAppearance, 
    platformInfo, 
    loading 
  } = useStore(subdomain);

  // Cart state from context
  const { setCartOpen } = useCart();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/public/store/${store.subdomain}/contact`, formData);
      toast.success("Your message has been sent successfully!");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.background_color }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: theme.primary_color, borderTopColor: 'transparent' }} />
          <p style={{ color: theme.text_color }}>Loading contact details...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Store Not Found</h1>
          <p className="text-slate-600 mb-4">The store you are looking for does not exist.</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Determine Store Address for Map and Display
  const hasBusinessAddress = store.business_address && Object.values(store.business_address).some(val => val !== "" && val !== "US");
  
  let mapAddressString = "";
  let displayAddress = "No address provided.";

  if (hasBusinessAddress) {
    const { address, city, state, zipcode } = store.business_address;
    const parts = [address, city, state, zipcode].filter(Boolean);
    mapAddressString = parts.join(", ");
    displayAddress = parts.join(", ");
  } else if (store.address) {
    mapAddressString = store.address;
    displayAddress = store.address;
  } else {
    mapAddressString = store.business_name; // fallback to searching business name
  }

  const mapIframeUrl = mapAddressString
    ? `https://maps.google.com/maps?q=${encodeURIComponent(mapAddressString)}&t=&z=14&ie=UTF8&iwloc=&output=embed`
    : "";

  return (
    <div className="animate-fade-in">
      {/* Main Content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 pt-12 pb-16">
          {/* Map Section */}
          {mapIframeUrl && (
            <div className="w-full h-[300px] md:h-[400px] bg-slate-200 rounded-2xl overflow-hidden shadow-md border border-slate-100 mb-16 animate-fade-in-up">
              <iframe
                title="Store Location"
                src={mapIframeUrl}
                className="w-full h-full border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Left Column: Contact Details */}
            <div className="space-y-8 animate-fade-in-up">
              <div>
                <h2 
                  className="text-3xl lg:text-4xl shadow-sm inline-block font-bold mb-4" 
                  style={{ color: theme.primary_color }}
                >
                  Contact Us
                </h2>
                <p className="text-lg text-slate-600 mb-8 max-w-md">
                  We're here to help and answer any question you might have. We look forward to hearing from you.
                </p>
              </div>

              <div className="space-y-6">
                
                {/* Address Card */}
                <div className="flex bg-white rounded-xl shadow-sm border border-slate-100 p-6 transition-transform hover:-translate-y-1 duration-300">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4" style={{ backgroundColor: `${theme.primary_color}15`, color: theme.primary_color }}>
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Address</h4>
                    <p className="text-lg font-medium" style={{ color: theme.primary_color }}>
                      {displayAddress}
                    </p>
                  </div>
                </div>

                {/* Phone Card */}
                {(store.contact_phone || storeAppearance?.top_nav_phone) && (
                  <div className="flex bg-white rounded-xl shadow-sm border border-slate-100 p-6 transition-transform hover:-translate-y-1 duration-300">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4" style={{ backgroundColor: `${theme.primary_color}15`, color: theme.primary_color }}>
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Phone</h4>
                      <p className="text-lg font-medium" style={{ color: theme.primary_color }}>
                        {store.contact_phone || storeAppearance?.top_nav_phone}
                      </p>
                    </div>
                  </div>
                )}

                {/* Email Card */}
                {(store.contact_email || store.business_email || storeAppearance?.top_nav_email) && (
                  <div className="flex bg-white rounded-xl shadow-sm border border-slate-100 p-6 transition-transform hover:-translate-y-1 duration-300">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4" style={{ backgroundColor: `${theme.primary_color}15`, color: theme.primary_color }}>
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Email</h4>
                      <p className="text-lg font-medium" style={{ color: theme.primary_color }}>
                        {store.contact_email || store.business_email || storeAppearance?.top_nav_email}
                      </p>
                    </div>
                  </div>
                )}
                
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 lg:p-10 relative overflow-hidden animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              <div 
                className="absolute top-0 right-0 w-32 h-32 opacity-5 pointer-events-none rounded-bl-full"
                style={{ backgroundColor: theme.primary_color }}
              ></div>
              
              <h2 className="text-2xl font-bold mb-2" style={{ color: theme.primary_color }}>
                Leave A Comment
              </h2>
              <p className="text-slate-500 mb-8">
                Our staff will reply shortly to answer your questions.
              </p>

              <form onSubmit={handleFormSubmit} className="space-y-6 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your name"
                      required
                      value={formData.name}
                      onChange={handleFormChange}
                      className="h-14 bg-slate-50/50 border-slate-200 focus-visible:ring-1"
                      style={{ '--tw-ring-color': theme.primary_color }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Input
                      type="email"
                      name="email"
                      placeholder="Your email"
                      required
                      value={formData.email}
                      onChange={handleFormChange}
                      className="h-14 bg-slate-50/50 border-slate-200 focus-visible:ring-1"
                      style={{ '--tw-ring-color': theme.primary_color }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Textarea
                    name="message"
                    placeholder="Your message"
                    required
                    value={formData.message}
                    onChange={handleFormChange}
                    className="min-h-[160px] resize-none bg-slate-50/50 border-slate-200 focus-visible:ring-1 p-4"
                    style={{ '--tw-ring-color': theme.primary_color }}
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={sending}
                  className="w-full h-14 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity rounded-lg"
                  style={{ backgroundColor: theme.primary_color }}
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      SEND MESSAGE
                    </>
                  )}
                </Button>
              </form>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}
